import { css } from "lit";

import type { Ref } from "lit/directives/ref.js";
import type { SlideSpotlightState } from "../types.d";

export interface SpotlightControllerProps {
  spotlightContainerRef: Ref<HTMLElement>;
}

export class SpotlightController {
  constructor({ spotlightContainerRef }: SpotlightControllerProps) {
    this._spotlightContainerRef = spotlightContainerRef;
  }
  private _spotlightContainerRef: Ref<HTMLElement>;
  private _contentEl?: HTMLElement;
  private _currentSpotlighted: Array<string> = [];
  set contentEl(el: HTMLElement) {
    this._contentEl = el;
  }
  update({ spotlighted = [] }: SlideSpotlightState) {
    const spotlightedSet = new Set(spotlighted);
    if (!this._contentEl) return;
    for (const id of this._currentSpotlighted) {
      if (!spotlightedSet.has(id))
        this._contentEl
          .querySelector(`[data-id="${id}"]`)
          ?.classList.remove("spotlighted");
    }
    if (spotlighted.length === 0) {
      this._spotlightContainerRef.value?.classList.remove(
        "spotlight-container-active",
      );
    } else {
      this._spotlightContainerRef.value?.classList.add(
        "spotlight-container-active",
      );
      const currentSpotlightedSet = new Set(this._currentSpotlighted);
      for (const id of spotlighted) {
        if (!currentSpotlightedSet.has(id))
          this._contentEl
            .querySelector(`[data-id="${id}"]`)
            ?.classList.add("spotlighted");
      }
    }
    this._currentSpotlighted = JSON.parse(JSON.stringify(spotlighted));
  }
}

export const styles = css`
  .spotlight-container {
    --fade-amount: 0;
  }

  .spotlight-container-active {
    --fade-amount: 0.8;
  }

  .spotlight-container {
    --spotlight-transition-duration: var(--duration-long);

    /* Adjustable Vars */
    --inverse-fade-amount: calc(1 - var(--fade-amount));
    --mixed-red: calc(var(--fade-amount) * var(--background-red));
    --mixed-green: calc(var(--fade-amount) * var(--background-green));
    --mixed-blue: calc(var(--fade-amount) * var(--background-blue));

    /* Utility Colors */
    --color-white: rgb(
      calc(255 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(255 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(255 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-black: var(--color-neutral-90);
    --color-gray: var(--color-neutral-50);
    --color-brand: rgb(
      calc(0 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(179 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(229 * var(--inverse-fade-amount) + var(--mixed-blue))
    );

    /* Color Rainbow */
    --color-neutral-05: rgb(
      calc(249 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(250 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(252 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-neutral-10: rgb(
      calc(228 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(232 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(237 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-neutral-20: rgb(
      calc(193 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(201 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(208 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-neutral-30: rgb(
      calc(164 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(171 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(183 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-neutral-40: rgb(
      calc(137 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(146 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(158 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-neutral-50: rgb(
      calc(107 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(115 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(132 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-neutral-60: rgb(
      calc(79 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(86 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(105 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-neutral-70: rgb(
      calc(62 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(64 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(84 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-neutral-80: rgb(
      calc(42 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(47 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(68 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-neutral-90: rgb(
      calc(24 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(29 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(54 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-red-05: rgb(
      calc(254 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(248 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(244 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-red-10: rgb(
      calc(249 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(227 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(222 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-red-20: rgb(
      calc(241 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(184 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(181 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-red-30: rgb(
      calc(234 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(137 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(140 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-red-40: rgb(
      calc(220 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(104 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(120 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-red-50: rgb(
      calc(190 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(73 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(103 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-red-60: rgb(
      calc(151 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(42 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(78 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-red-70: rgb(
      calc(117 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(31 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(64 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-red-80: rgb(
      calc(86 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(23 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(57 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-red-90: rgb(
      calc(60 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(10 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(39 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-orange-05: rgb(
      calc(254 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(250 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(238 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-orange-10: rgb(
      calc(250 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(228 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(196 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-orange-20: rgb(
      calc(239 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(186 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(141 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-orange-30: rgb(
      calc(230 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(149 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(101 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-orange-40: rgb(
      calc(214 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(117 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(82 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-orange-50: rgb(
      calc(181 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(84 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(58 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-orange-60: rgb(
      calc(145 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(54 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(46 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-orange-70: rgb(
      calc(116 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(38 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(38 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-orange-80: rgb(
      calc(87 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(25 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(31 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-orange-90: rgb(
      calc(59 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(17 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(19 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-yellow-05: rgb(
      calc(251 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(249 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(235 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-yellow-10: rgb(
      calc(245 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(229 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(189 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-yellow-20: rgb(
      calc(232 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(194 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(129 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-yellow-30: rgb(
      calc(217 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(157 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(82 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-yellow-40: rgb(
      calc(203 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(125 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(51 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-yellow-50: rgb(
      calc(175 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(89 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(34 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-yellow-60: rgb(
      calc(141 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(61 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(23 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-yellow-70: rgb(
      calc(108 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(47 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(20 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-yellow-80: rgb(
      calc(80 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(34 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(17 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-yellow-90: rgb(
      calc(53 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(22 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(9 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-green-05: rgb(
      calc(242 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(254 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(240 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-green-10: rgb(
      calc(211 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(242 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(205 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-green-20: rgb(
      calc(151 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(214 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(156 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-green-30: rgb(
      calc(96 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(191 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(135 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-green-40: rgb(
      calc(79 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(164 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(120 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-green-50: rgb(
      calc(58 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(128 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(96 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-green-60: rgb(
      calc(43 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(97 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(70 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-green-70: rgb(
      calc(32 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(75 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(60 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-green-80: rgb(
      calc(23 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(54 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(52 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-green-90: rgb(
      calc(14 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(34 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(39 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-turquoise-05: rgb(
      calc(236 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(251 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(254 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-turquoise-10: rgb(
      calc(185 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(239 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(247 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-turquoise-20: rgb(
      calc(135 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(222 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(234 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-turquoise-30: rgb(
      calc(76 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(193 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(209 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-turquoise-40: rgb(
      calc(31 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(156 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(173 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-turquoise-50: rgb(
      calc(9 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(125 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(141 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-turquoise-60: rgb(
      calc(0 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(98 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(111 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-turquoise-70: rgb(
      calc(0 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(77 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(88 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-turquoise-80: rgb(
      calc(0 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(54 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(62 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-turquoise-90: rgb(
      calc(0 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(41 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(48 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-cyan-05: rgb(
      calc(239 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(252 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(252 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-cyan-10: rgb(
      calc(204 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(241 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(250 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-cyan-20: rgb(
      calc(145 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(209 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(234 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-cyan-30: rgb(
      calc(105 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(180 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(226 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-cyan-40: rgb(
      calc(84 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(150 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(207 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-cyan-50: rgb(
      calc(54 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(120 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(180 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-cyan-60: rgb(
      calc(36 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(87 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(145 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-cyan-70: rgb(
      calc(27 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(69 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(117 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-cyan-80: rgb(
      calc(21 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(49 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(79 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-cyan-90: rgb(
      calc(11 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(33 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(49 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-blue-05: rgb(
      calc(245 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(251 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(255 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-blue-10: rgb(
      calc(217 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(236 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(253 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-blue-20: rgb(
      calc(172 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(204 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(251 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-blue-30: rgb(
      calc(133 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(170 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(241 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-blue-40: rgb(
      calc(114 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(140 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(230 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-blue-50: rgb(
      calc(88 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(105 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(205 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-blue-60: rgb(
      calc(64 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(76 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(166 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-blue-70: rgb(
      calc(49 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(60 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(134 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-blue-80: rgb(
      calc(35 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(44 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(97 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-blue-90: rgb(
      calc(19 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(30 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(63 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-violet-05: rgb(
      calc(248 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(249 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(254 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-violet-10: rgb(
      calc(230 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(230 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(250 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-violet-20: rgb(
      calc(196 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(195 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(232 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-violet-30: rgb(
      calc(172 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(161 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(220 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-violet-40: rgb(
      calc(152 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(130 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(213 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-violet-50: rgb(
      calc(126 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(98 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(188 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-violet-60: rgb(
      calc(93 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(72 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(151 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-violet-70: rgb(
      calc(71 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(52 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(126 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-violet-80: rgb(
      calc(51 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(37 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(96 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-violet-90: rgb(
      calc(29 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(23 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(75 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-purple-05: rgb(
      calc(253 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(248 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(255 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-purple-10: rgb(
      calc(247 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(225 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(245 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-purple-20: rgb(
      calc(230 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(182 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(225 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-purple-30: rgb(
      calc(215 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(145 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(216 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-purple-40: rgb(
      calc(189 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(114 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(203 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-purple-50: rgb(
      calc(152 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(85 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(175 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-purple-60: rgb(
      calc(113 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(60 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(146 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-purple-70: rgb(
      calc(85 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(45 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(125 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-purple-80: rgb(
      calc(59 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(30 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(102 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-purple-90: rgb(
      calc(40 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(15 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(82 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-tan-05: rgb(
      calc(249 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(247 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(241 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-tan-10: rgb(
      calc(239 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(232 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(219 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-tan-20: rgb(
      calc(215 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(202 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(176 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-tan-30: rgb(
      calc(187 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(170 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(136 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-tan-40: rgb(
      calc(163 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(145 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(110 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-tan-50: rgb(
      calc(129 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(111 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(77 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-tan-60: rgb(
      calc(100 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(85 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(55 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-tan-70: rgb(
      calc(80 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(67 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(41 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-tan-80: rgb(
      calc(59 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(49 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(28 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-tan-90: rgb(
      calc(37 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(30 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(17 * var(--inverse-fade-amount) + var(--mixed-blue))
    );

    /* Highlight Colors */
    --color-highlight-yellow-1: rgb(
      calc(255 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(227 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(79 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-highlight-yellow-2: rgb(
      calc(255 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(247 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(44 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-highlight-pink-1: rgb(
      calc(255 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(61 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(201 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-highlight-pink-2: rgb(
      calc(230 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(128 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(255 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-highlight-orange-1: rgb(
      calc(255 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(140 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(57 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-highlight-orange-2: rgb(
      calc(255 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(192 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(70 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-highlight-blue-1: rgb(
      calc(79 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(223 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(255 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-highlight-blue-2: rgb(
      calc(84 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(255 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(214 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-highlight-green-1: rgb(
      calc(41 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(248 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(62 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
    --color-highlight-green-2: rgb(
      calc(175 * var(--inverse-fade-amount) + var(--mixed-red)),
      calc(255 * var(--inverse-fade-amount) + var(--mixed-green)),
      calc(44 * var(--inverse-fade-amount) + var(--mixed-blue))
    );
  }

  .spotlighted {
    --fade-amount: 0;
    --inverse-fade-amount: 1;

    /* Utility Colors */
    --color-white: #ffffff;
    --color-black: var(--color-neutral-90);
    --color-gray: var(--color-neutral-50);
    --color-brand: #00b3e5;

    /* Color Rainbow */
    --color-neutral-05: #f9fafc;
    --color-neutral-10: #e4e8ed;
    --color-neutral-20: #c1c9d0;
    --color-neutral-30: #a4abb7;
    --color-neutral-40: #89929e;
    --color-neutral-50: #6b7384;
    --color-neutral-60: #4f5669;
    --color-neutral-70: #3e4054;
    --color-neutral-80: #2a2f44;
    --color-neutral-90: #181d36;
    --color-red-05: #fef8f4;
    --color-red-10: #f9e3de;
    --color-red-20: #f1b8b5;
    --color-red-30: #ea898c;
    --color-red-40: #dc6878;
    --color-red-50: #be4967;
    --color-red-60: #972a4e;
    --color-red-70: #751f40;
    --color-red-80: #561739;
    --color-red-90: #3c0a27;
    --color-orange-05: #fefaee;
    --color-orange-10: #fae4c4;
    --color-orange-20: #efba8d;
    --color-orange-30: #e69565;
    --color-orange-40: #d67552;
    --color-orange-50: #b5543a;
    --color-orange-60: #91362e;
    --color-orange-70: #742626;
    --color-orange-80: #57191f;
    --color-orange-90: #3b1113;
    --color-yellow-05: #fbf9eb;
    --color-yellow-10: #f5e5bd;
    --color-yellow-20: #e8c281;
    --color-yellow-30: #d99d52;
    --color-yellow-40: #cb7d33;
    --color-yellow-50: #af5922;
    --color-yellow-60: #8d3d17;
    --color-yellow-70: #6c2f14;
    --color-yellow-80: #502211;
    --color-yellow-90: #351609;
    --color-green-05: #f2fef0;
    --color-green-10: #d3f2cd;
    --color-green-20: #97d69c;
    --color-green-30: #60bf87;
    --color-green-40: #4fa478;
    --color-green-50: #3a8060;
    --color-green-60: #2b6146;
    --color-green-70: #204b3c;
    --color-green-80: #173634;
    --color-green-90: #0e2227;
    --color-turquoise-05: #ecfbfe;
    --color-turquoise-10: #b9eff7;
    --color-turquoise-20: #87deea;
    --color-turquoise-30: #4cc1d1;
    --color-turquoise-40: #1f9cad;
    --color-turquoise-50: #097d8d;
    --color-turquoise-60: #00626f;
    --color-turquoise-70: #004d58;
    --color-turquoise-80: #00363e;
    --color-turquoise-90: #002930;
    --color-cyan-05: #effcfc;
    --color-cyan-10: #ccf1fa;
    --color-cyan-20: #91d1ea;
    --color-cyan-30: #69b4e2;
    --color-cyan-40: #5496cf;
    --color-cyan-50: #3678b4;
    --color-cyan-60: #245791;
    --color-cyan-70: #1b4575;
    --color-cyan-80: #15314f;
    --color-cyan-90: #0b2131;
    --color-blue-05: #f5fbff;
    --color-blue-10: #d9ecfd;
    --color-blue-20: #acccfb;
    --color-blue-30: #85aaf1;
    --color-blue-40: #728ce6;
    --color-blue-50: #5869cd;
    --color-blue-60: #404ca6;
    --color-blue-70: #313c86;
    --color-blue-80: #232c61;
    --color-blue-90: #131e3f;
    --color-violet-05: #f8f9fe;
    --color-violet-10: #e6e6fa;
    --color-violet-20: #c4c3e8;
    --color-violet-30: #aca1dc;
    --color-violet-40: #9882d5;
    --color-violet-50: #7e62bc;
    --color-violet-60: #5d4897;
    --color-violet-70: #47347e;
    --color-violet-80: #332560;
    --color-violet-90: #1d174b;
    --color-purple-05: #fdf8ff;
    --color-purple-10: #f7e1f5;
    --color-purple-20: #e6b6e1;
    --color-purple-30: #d791d8;
    --color-purple-40: #bd72cb;
    --color-purple-50: #9855af;
    --color-purple-60: #713c92;
    --color-purple-70: #552d7d;
    --color-purple-80: #3b1e66;
    --color-purple-90: #280f52;
    --color-tan-05: #f9f7f1;
    --color-tan-10: #efe8db;
    --color-tan-20: #d7cab0;
    --color-tan-30: #bbaa88;
    --color-tan-40: #a3916e;
    --color-tan-50: #816f4d;
    --color-tan-60: #645537;
    --color-tan-70: #504329;
    --color-tan-80: #3b311c;
    --color-tan-90: #251e11;

    /* Highlight Colors */
    --color-highlight-yellow-1: #ffe34f;
    --color-highlight-yellow-2: #fff72c;
    --color-highlight-pink-1: #ff3dc9;
    --color-highlight-pink-2: #e680ff;
    --color-highlight-orange-1: #ff8c39;
    --color-highlight-orange-2: #ffc046;
    --color-highlight-blue-1: #4fdfff;
    --color-highlight-blue-2: #54ffd6;
    --color-highlight-green-1: #29f83e;
    --color-highlight-green-2: #afff2c;
  }
`;
